import React, { useCallback, useEffect, useState, useRef } from 'react';
interface SwipeTestProps {
  onComplete: (correct: number, total: number, timeSeconds: number) => void;
}
type Decision = 'keep' | 'toss';
type AppType = 'letlois-lead' | 'letlois-booking' | 'google-review' | 'slack' | 'instagram' | 'facebook' | 'meta-ads' | 'missed-call' | 'yelp' | 'email-lead';
interface NotificationCard {
  id: string;
  appType: AppType;
  appName: string;
  time: string;
  title: string;
  body: string;
  correctAnswer: Decision;
  stars?: number;
  isVoicemail?: boolean;
  voicemailText?: string;
}
// LetLois SVG logo mark (spark/bolt shape)
function LetLoisIcon({
  size = 28
}: {
  size?: number;
}) {
  return <div className="rounded-xl flex items-center justify-center flex-shrink-0" style={{
    width: size,
    height: size,
    background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)'
  }} data-id="element-133">
      <svg width={size * 0.6} height={size * 0.6} viewBox="0 0 24 24" fill="none" data-id="element-134">
        <path d="M13 2L4.5 13.5H11L10 22L19.5 10.5H13L13 2Z" fill="white" stroke="white" strokeWidth="0.5" strokeLinejoin="round" data-id="element-135" />
      </svg>
    </div>;
}
function AppIcon({
  appType,
  size = 28
}: {
  appType: AppType;
  size?: number;
}) {
  const s = size;
  const r = Math.round(s * 0.28);
  if (appType === 'letlois-lead' || appType === 'letlois-booking') {
    return <LetLoisIcon size={s} data-id="element-136" />;
  }
  const configs: Record<string, {
    bg: string;
    content: React.ReactNode;
  }> = {
    'google-review': {
      bg: 'white',
      content: <svg viewBox="0 0 24 24" width={s * 0.65} height={s * 0.65} data-id="element-137">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" data-id="element-138" />
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" data-id="element-139" />
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" data-id="element-140" />
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" data-id="element-141" />
        </svg>
    },
    slack: {
      bg: '#4A154B',
      content: <svg viewBox="0 0 24 24" width={s * 0.6} height={s * 0.6} fill="none" data-id="element-142">
          <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" fill="white" data-id="element-143" />
        </svg>
    },
    instagram: {
      bg: 'transparent',
      content: <div className="rounded-xl flex items-center justify-center" style={{
        width: s,
        height: s,
        background: 'radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%,#d6249f 60%,#285AEB 90%)'
      }} data-id="element-144">
          <svg viewBox="0 0 24 24" width={s * 0.6} height={s * 0.6} fill="white" data-id="element-145">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" data-id="element-146" />
          </svg>
        </div>
    },
    facebook: {
      bg: '#1877F2',
      content: <svg viewBox="0 0 24 24" width={s * 0.55} height={s * 0.55} fill="white" data-id="element-147">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" data-id="element-148" />
        </svg>
    },
    'meta-ads': {
      bg: '#0866FF',
      content: <svg viewBox="0 0 24 24" width={s * 0.6} height={s * 0.6} fill="white" data-id="element-149">
          <path d="M6.915 4.03c-1.968 0-3.683 1.28-4.871 3.113C.704 9.208 0 11.883 0 14.449c0 .706.07 1.369.21 1.973a6.624 6.624 0 0 0 .265.86 5.297 5.297 0 0 0 .371.761c.696 1.159 1.818 1.927 3.593 1.927 1.497 0 2.633-.671 3.965-2.444.76-1.012 1.144-1.626 2.663-4.32l.756-1.339.186-.325c.061.1.121.196.183.3l2.152 3.595c.724 1.21 1.665 2.556 2.47 3.314 1.046.987 1.992 1.22 2.93 1.22 1.385 0 2.452-.701 3.279-1.86.498-.706.732-1.523.914-2.458.162-.832.23-1.73.23-2.65 0-2.015-.503-4.38-1.62-6.027C21.2 5.29 19.587 4.03 17.7 4.03c-1.502 0-2.757.64-3.957 2.172-.764.982-1.197 1.753-2.033 3.308-.1.182-.196.356-.29.527l-.29-.527c-.836-1.555-1.27-2.326-2.033-3.308C7.907 4.67 6.652 4.03 5.15 4.03H6.915zm10.785 2.147c1.08 0 2.116.81 2.893 2.28.747 1.418 1.168 3.387 1.168 5.22 0 .73-.07 1.48-.23 2.11-.16.63-.38 1.09-.65 1.36-.27.27-.55.4-.9.4-.42 0-.87-.17-1.43-.67-.56-.5-1.27-1.52-2.07-2.85l-2.15-3.59c-.18-.3-.36-.6-.54-.9.18-.3.36-.6.54-.9 1.08-1.8 1.98-3.46 3.37-3.46zm-12.5 0c1.39 0 2.29 1.66 3.37 3.46.18.3.36.6.54.9-.18.3-.36.6-.54.9l-2.15 3.59c-.8 1.33-1.51 2.35-2.07 2.85-.56.5-1.01.67-1.43.67-.35 0-.63-.13-.9-.4-.27-.27-.49-.73-.65-1.36-.16-.63-.23-1.38-.23-2.11 0-1.833.421-3.802 1.168-5.22.777-1.47 1.813-2.28 2.893-2.28z" data-id="element-150" />
        </svg>
    },
    'missed-call': {
      bg: '#1C1C1E',
      content: <svg viewBox="0 0 24 24" width={s * 0.6} height={s * 0.6} fill="#FF3B30" data-id="element-151">
          <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" data-id="element-152" />
        </svg>
    },
    yelp: {
      bg: '#D32323',
      content: <svg viewBox="0 0 24 24" width={s * 0.6} height={s * 0.6} fill="white" data-id="element-153">
          <path d="M20.16 12.73l-4.703 1.14c-.847.205-1.593-.593-1.291-1.416l1.755-4.583c.261-.682 1.083-.88 1.628-.38 1.458 1.335 2.392 3.44 2.392 5.64 0 .206-.007.41-.02.612l.239-.013zM12.5 7.5c0-.828.672-1.5 1.5-1.5h.5V3.5C14.5 2.119 13.381 1 12 1S9.5 2.119 9.5 3.5V6h.5c.828 0 1.5.672 1.5 1.5zM10.5 21.5c0 .828.672 1.5 1.5 1.5s1.5-.672 1.5-1.5v-9c0-.828-.672-1.5-1.5-1.5s-1.5.672-1.5 1.5v9z" data-id="element-154" />
        </svg>
    },
    'email-lead': {
      bg: '#EA4335',
      content: <svg viewBox="0 0 24 24" width={s * 0.6} height={s * 0.6} fill="white" data-id="element-155">
          <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" data-id="element-156" />
        </svg>
    }
  };
  const cfg = configs[appType];
  if (!cfg) return null;
  if (appType === 'instagram') return <>{cfg.content}</>;
  return <div className="rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden" style={{
    width: s,
    height: s,
    background: cfg.bg,
    borderRadius: Math.round(s * 0.22)
  }} data-id="element-157">
      {cfg.content}
    </div>;
}
function StarRating({
  stars
}: {
  stars: number;
}) {
  return <div className="flex gap-0.5 mt-0.5" data-id="element-158">
      {[1, 2, 3, 4, 5].map(i => <svg key={i} className={`w-3 h-3 ${i <= stars ? 'text-[#FBBC04]' : 'text-gray-300'}`} viewBox="0 0 20 20" fill="currentColor" data-id="element-159">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-160" />
        </svg>)}
    </div>;
}
function NotificationCardUI({
  card
}: {
  card: NotificationCard;
}) {
  const isLetLois = card.appType === 'letlois-lead' || card.appType === 'letlois-booking';
  return <div className={`w-full rounded-2xl overflow-hidden shadow-xl ${isLetLois ? 'ring-2 ring-violet-500/40' : ''}`} style={{
    background: 'rgba(28,28,30,0.95)',
    backdropFilter: 'blur(20px)'
  }} data-id="element-161">
      {/* Notification header */}
      <div className="flex items-center gap-2.5 px-4 pt-3 pb-2" data-id="element-162">
        <AppIcon appType={card.appType} size={32} data-id="element-163" />
        <div className="flex-1 min-w-0" data-id="element-164">
          <div className="flex items-center justify-between" data-id="element-165">
            <span className={`text-xs font-semibold uppercase tracking-wide ${isLetLois ? 'text-violet-400' : 'text-gray-400'}`} data-id="element-166">
              {card.appName}
            </span>
            <span className="text-gray-500 text-xs" data-id="element-167">{card.time}</span>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="h-px bg-zinc-700/50 mx-4" data-id="element-168" />

      {/* Notification body */}
      <div className="px-4 py-3" data-id="element-169">
        <p className={`font-semibold text-sm leading-snug mb-1 ${isLetLois ? 'text-violet-200' : 'text-white'}`} data-id="element-170">
          {card.title}
        </p>

        {card.stars !== undefined && <StarRating stars={card.stars} data-id="element-171" />}

        <p className="text-gray-300 text-sm leading-relaxed mt-1" data-id="element-172">
          {card.body}
        </p>

        {card.isVoicemail && card.voicemailText && <div className="mt-2 bg-zinc-800/80 rounded-lg p-3 border border-zinc-700/50" data-id="element-173">
            <div className="flex items-center gap-1.5 mb-1.5" data-id="element-174">
              <div className="w-1.5 h-1.5 rounded-full bg-red-400" data-id="element-175" />
              <span className="text-red-400 text-xs font-bold uppercase tracking-wider" data-id="element-176">
                Voicemail Transcript
              </span>
            </div>
            <p className="text-gray-300 text-xs leading-relaxed italic" data-id="element-177">
              "{card.voicemailText}"
            </p>
          </div>}
      </div>

      {/* LetLois special glow footer */}
      {isLetLois && <div className="px-4 pb-3" data-id="element-178">
          <div className="flex items-center gap-1.5 bg-violet-900/30 rounded-lg px-3 py-1.5 border border-violet-700/30" data-id="element-179">
            <LetLoisIcon size={14} data-id="element-180" />
            <span className="text-violet-300 text-xs font-bold" data-id="element-181">LetLois</span>
            <span className="text-violet-400 text-xs" data-id="element-182">· Tap to respond</span>
          </div>
        </div>}
    </div>;
}
const ALL_SCENARIOS: NotificationCard[] = [
// LetLois — New Leads (KEEP)
{
  id: 'll-lead-1',
  appType: 'letlois-lead',
  appName: 'LetLois',
  time: 'now',
  title: '⚡ New Lead — Sarah M.',
  body: "Hey! I want to book. What's available?",
  correctAnswer: 'keep'
}, {
  id: 'll-lead-2',
  appType: 'letlois-lead',
  appName: 'LetLois',
  time: '1m ago',
  title: '⚡ New Lead — James T.',
  body: 'Friend referred me. Ready to start.',
  correctAnswer: 'keep'
}, {
  id: 'll-lead-3',
  appType: 'letlois-lead',
  appName: 'LetLois',
  time: '2m ago',
  title: '⚡ New Lead — Priya K.',
  body: 'Can we talk today? I want to book ASAP.',
  correctAnswer: 'keep'
},
// LetLois — New Bookings (KEEP)
{
  id: 'll-book-1',
  appType: 'letlois-booking',
  appName: 'LetLois',
  time: 'just now',
  title: '📅 New Booking Confirmed!',
  body: 'Marcus D. — Thu 2:00 PM. $250 deposit collected.',
  correctAnswer: 'keep'
}, {
  id: 'll-book-2',
  appType: 'letlois-booking',
  appName: 'LetLois',
  time: '3m ago',
  title: '📅 Appointment Booked',
  body: 'Taylor R. — Fri 10 AM. Full payment received.',
  correctAnswer: 'keep'
},
// Google Reviews — 5 star (KEEP)
{
  id: 'g5-1',
  appType: 'google-review',
  appName: 'Google Reviews',
  time: '5m ago',
  title: 'Sarah M. left a review',
  body: 'Incredible experience. Professional and fast. 100% coming back!',
  stars: 5,
  correctAnswer: 'keep'
}, {
  id: 'g5-2',
  appType: 'google-review',
  appName: 'Google Reviews',
  time: '12m ago',
  title: 'Marcus D. left a review',
  body: 'Best in the business. Nothing else comes close.',
  stars: 5,
  correctAnswer: 'keep'
},
// Google Reviews — 1 star (TOSS)
{
  id: 'g1-1',
  appType: 'google-review',
  appName: 'Google Reviews',
  time: '8m ago',
  title: 'Angry Customer left a review',
  body: 'Waited an hour. Nobody apologized. Total waste. AVOID.',
  stars: 1,
  correctAnswer: 'toss'
}, {
  id: 'g1-2',
  appType: 'google-review',
  appName: 'Google Reviews',
  time: '20m ago',
  title: 'Disappointed Buyer left a review',
  body: 'Arrived broken. No response after 3 messages.',
  stars: 1,
  correctAnswer: 'toss'
},
// Slack — good (KEEP)
{
  id: 'sl-good-1',
  appType: 'slack',
  appName: 'Slack',
  time: '9:47 PM',
  title: 'Alex in #team-chat',
  body: 'Stayed late to finish the job. All done! 🙏',
  correctAnswer: 'keep'
}, {
  id: 'sl-good-2',
  appType: 'slack',
  appName: 'Slack',
  time: '3:30 PM',
  title: 'Taylor in #team-chat',
  body: 'Handled an upset customer. They left a 5-star review 😊',
  correctAnswer: 'keep'
},
// Slack — bad (TOSS)
{
  id: 'sl-bad-1',
  appType: 'slack',
  appName: 'Slack',
  time: '7:02 AM',
  title: 'Riley in #team-chat',
  body: "Don't feel like coming in today lol",
  correctAnswer: 'toss'
}, {
  id: 'sl-bad-2',
  appType: 'slack',
  appName: 'Slack',
  time: '2:44 PM',
  title: 'Morgan in #team-chat',
  body: 'Sent wrong orders to 12 customers. Oops 😬',
  correctAnswer: 'toss'
},
// Instagram DM (KEEP)
{
  id: 'ig-1',
  appType: 'instagram',
  appName: 'Instagram',
  time: '4m ago',
  title: 'DM from @jessica.m',
  body: 'How do I book?? I need this!',
  correctAnswer: 'keep'
},
// Facebook (KEEP)
{
  id: 'fb-1',
  appType: 'facebook',
  appName: 'Facebook',
  time: '15m ago',
  title: 'Message from David R.',
  body: 'Saw your ad. Can you send pricing?',
  correctAnswer: 'keep'
},
// Meta Ads — positive (KEEP)
{
  id: 'meta-1',
  appType: 'meta-ads',
  appName: 'Meta Business Suite',
  time: '6m ago',
  title: 'New comment on your ad',
  body: 'This is exactly what I need! Just DMed you 🙏',
  correctAnswer: 'keep'
},
// Meta Ads — negative (TOSS)
{
  id: 'meta-2',
  appType: 'meta-ads',
  appName: 'Meta Business Suite',
  time: '18m ago',
  title: 'New comment on your ad',
  body: 'Scam! Never received anything. Reporting this page.',
  correctAnswer: 'toss'
},
// Missed call — KEEP
{
  id: 'call-1',
  appType: 'missed-call',
  appName: 'Phone',
  time: '11m ago',
  title: 'Missed Call + Voicemail',
  body: '(305) 847-2291',
  isVoicemail: true,
  voicemailText: 'Got your number from a friend. Ready to book and pay today. Call me back!',
  correctAnswer: 'keep'
},
// Missed call — TOSS
{
  id: 'call-2',
  appType: 'missed-call',
  appName: 'Phone',
  time: '25m ago',
  title: 'Missed Call + Voicemail',
  body: '(800) 555-0199',
  isVoicemail: true,
  voicemailText: 'Automated message: outstanding balance on your account. Press 1 or face legal action.',
  correctAnswer: 'toss'
}];
const CARDS_PER_GAME = 6;
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
export function SwipeTest({
  onComplete
}: SwipeTestProps) {
  const [started, setStarted] = useState(false);
  const [cards] = useState<NotificationCard[]>(() => shuffle(ALL_SCENARIOS).slice(0, Math.min(CARDS_PER_GAME, ALL_SCENARIOS.length)));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [swipeResult, setSwipeResult] = useState<Decision | null>(null);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const startTimeRef = useRef<number>(0);
  const startXRef = useRef(0);
  const currentCard = cards[currentIndex];
  const isDone = currentIndex >= cards.length;
  useEffect(() => {
    if (isDone && started) {
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      onComplete(correct, cards.length, elapsed);
    }
  }, [isDone, started]);
  const triggerSwipe = useCallback((decision: Decision) => {
    if (isAnimatingOut || !currentCard) return;
    const isCorrect = decision === currentCard.correctAnswer;
    if (isCorrect) setCorrect(c => c + 1);
    setCurrentIndex(i => i + 1);
    setDragX(0);
    setSwipeResult(null);
    setIsAnimatingOut(false);
  }, [isAnimatingOut, currentCard]);
  const onTouchStart = (e: React.TouchEvent) => {
    if (!started || isAnimatingOut) return;
    startXRef.current = e.touches[0].clientX;
    setIsDragging(true);
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    setDragX(e.touches[0].clientX - startXRef.current);
  };
  const onTouchEnd = () => {
    setIsDragging(false);
    if (Math.abs(dragX) > 80) triggerSwipe(dragX < 0 ? 'toss' : 'keep');else setDragX(0);
  };
  const onMouseDown = (e: React.MouseEvent) => {
    if (!started || isAnimatingOut) return;
    startXRef.current = e.clientX;
    setIsDragging(true);
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setDragX(e.clientX - startXRef.current);
  };
  const onMouseUp = () => {
    setIsDragging(false);
    if (Math.abs(dragX) > 80) triggerSwipe(dragX < 0 ? 'toss' : 'keep');else setDragX(0);
  };
  const rotation = dragX / 18;
  const keepOpacity = Math.max(0, Math.min(1, dragX / 120));
  const tossOpacity = Math.max(0, Math.min(1, -dragX / 120));
  const getCardTransform = () => {
    if (isAnimatingOut) {
      const dir = swipeResult === 'keep' ? 1 : -1;
      return `translateX(${dir * 500}px) rotate(${dir * 25}deg)`;
    }
    return `translateX(${dragX}px) rotate(${rotation}deg)`;
  };
  if (!started) {
    return <div className="h-full flex flex-col items-center justify-center text-center p-6" data-id="element-183">
        <div className="text-5xl mb-4" data-id="element-184">📱</div>
        <h2 className="text-3xl font-mono font-bold text-cyan-400 mb-2" data-id="element-185">
          KEEP OR TOSS?
        </h2>
        <p className="text-gray-400 mb-4 max-w-sm text-sm" data-id="element-186">
          {cards.length} real business notifications. Swipe{' '}
          <span className="text-green-400 font-bold" data-id="element-187">RIGHT to KEEP</span>, swipe{' '}
          <span className="text-red-400 font-bold" data-id="element-188">LEFT to TOSS</span>.
        </p>
        <p className="text-gray-500 mb-8 max-w-sm text-xs" data-id="element-189">
          Review each notification like a real business owner. Your time starts
          when you tap Begin.
        </p>
        <div className="flex gap-8 mb-8 text-sm" data-id="element-190">
          <div className="flex flex-col items-center gap-1" data-id="element-191">
            <div className="w-12 h-12 rounded-full bg-red-500/20 border-2 border-red-500 flex items-center justify-center text-xl" data-id="element-192">
              👈
            </div>
            <span className="text-red-400 font-bold" data-id="element-193">TOSS</span>
          </div>
          <div className="flex flex-col items-center gap-1" data-id="element-194">
            <div className="w-12 h-12 rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center text-xl" data-id="element-195">
              👉
            </div>
            <span className="text-green-400 font-bold" data-id="element-196">KEEP</span>
          </div>
        </div>
        <button onClick={() => {
        setStarted(true);
        startTimeRef.current = Date.now();
      }} className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold uppercase tracking-wider px-10 py-4 rounded-sm text-lg transition-colors active:scale-95" data-id="element-197">
          BEGIN — TIMER STARTS NOW
        </button>
      </div>;
  }
  return <div className="h-full flex flex-col w-full max-w-sm mx-auto select-none" data-id="element-198">
      <div className="flex justify-between items-center mb-3 px-2" data-id="element-199">
        <div className="text-sm font-mono text-gray-400" data-id="element-200">
          {Math.min(currentIndex + 1, cards.length)}/{cards.length}
        </div>
        <div className="text-sm font-mono text-cyan-400" data-id="element-201">{correct} ✓</div>
      </div>

      <div className="flex justify-between px-4 mb-2 text-xs font-bold uppercase tracking-wider" data-id="element-202">
        <span className="text-red-400" data-id="element-203">← TOSS</span>
        <span className="text-green-400" data-id="element-204">KEEP →</span>
      </div>

      <div className="flex-1 relative flex items-center justify-center overflow-hidden" data-id="element-205">
        {isDone ? <div className="text-center" data-id="element-206">
            <div className="text-4xl mb-2" data-id="element-207">✅</div>
            <p className="text-white font-mono text-xl" data-id="element-208">Calculating...</p>
          </div> : <div className="w-full cursor-grab active:cursor-grabbing" style={{
        transform: getCardTransform(),
        transition: isDragging ? 'none' : isAnimatingOut ? 'transform 0.3s ease-out' : 'transform 0.15s ease-out'
      }} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp} data-id="element-209">
            {/* TOSS overlay */}
            <div className="absolute inset-0 rounded-2xl bg-red-500/20 border-4 border-red-500 z-10 flex items-center justify-center pointer-events-none" style={{
          opacity: tossOpacity,
          transition: isDragging ? 'none' : 'opacity 0.15s'
        }} data-id="element-210">
              <span className="text-red-400 text-4xl font-black rotate-[-15deg] border-4 border-red-400 px-4 py-1 rounded" data-id="element-211">
                TOSS
              </span>
            </div>
            {/* KEEP overlay */}
            <div className="absolute inset-0 rounded-2xl bg-green-500/20 border-4 border-green-500 z-10 flex items-center justify-center pointer-events-none" style={{
          opacity: keepOpacity,
          transition: isDragging ? 'none' : 'opacity 0.15s'
        }} data-id="element-212">
              <span className="text-green-400 text-4xl font-black rotate-[15deg] border-4 border-green-400 px-4 py-1 rounded" data-id="element-213">
                KEEP
              </span>
            </div>
            <NotificationCardUI card={currentCard} data-id="element-214" />
          </div>}
        {!isDone && currentIndex + 1 < cards.length && <div className="absolute inset-0 -z-10 scale-95 opacity-30 pointer-events-none" data-id="element-215">
            <NotificationCardUI card={cards[currentIndex + 1]} data-id="element-216" />
          </div>}
      </div>

      <div className="flex gap-3 mt-4 px-2 pb-2" data-id="element-217">
        <button onClick={() => triggerSwipe('toss')} disabled={isAnimatingOut || isDone} className="flex-1 py-4 rounded-lg bg-red-500/20 border-2 border-red-500 text-red-400 font-black uppercase tracking-wider text-lg active:scale-95 transition-transform disabled:opacity-40" data-id="element-218">
          👈 TOSS
        </button>
        <button onClick={() => triggerSwipe('keep')} disabled={isAnimatingOut || isDone} className="flex-1 py-4 rounded-lg bg-green-500/20 border-2 border-green-500 text-green-400 font-black uppercase tracking-wider text-lg active:scale-95 transition-transform disabled:opacity-40" data-id="element-219">
          KEEP 👉
        </button>
      </div>
    </div>;
}